import { useElementSize, useLocalStorage } from '@vueuse/core';
import {
    computed,
    ref,
    unref,
    watch,
    watchEffect,
    type ComputedRef,
    type CSSProperties,
    type MaybeRef,
    type Ref,
} from 'vue';

const DEFAULT_MIN_COMPANION_WIDTH_PX = 420;

type Options = {
    enabled: Ref<boolean>;
    storageKey: string;
    defaultWidth?: number;
    minWidth: number;
    side?: 'start' | 'end';
    minCompanionWidth?: MaybeRef<number>;
};

export type ResizablePane = {
    containerRef: Ref<HTMLElement | null>;
    panelRef: Ref<HTMLElement | null>;
    width: Ref<number | null>;
    isResizing: Ref<boolean>;
    panelStyle: ComputedRef<CSSProperties | undefined>;
    handleStyle: ComputedRef<CSSProperties>;
    startResize: (event: MouseEvent | TouchEvent) => void;
};

const readClientX = (event: MouseEvent | TouchEvent): number | null => {
    if ('touches' in event) {
        return event.touches[0]?.clientX ?? event.changedTouches[0]?.clientX ?? null;
    }
    return event.clientX;
};

const parsePx = (value: string): number => parseFloat(value) || 0;

export const useResizablePane = (options: Options): ResizablePane => {
    const side = options.side ?? 'start';

    const containerRef = ref<HTMLElement | null>(null);
    const panelRef = ref<HTMLElement | null>(null);
    const { width: containerWidth } = useElementSize(containerRef);
    const { width: panelWidth } = useElementSize(panelRef);

    const gapPx = ref(0);
    const inlinePaddingPx = ref(0);
    const blockStartPaddingPx = ref(0);
    const blockEndPaddingPx = ref(0);

    watchEffect(() => {
        // Re-measure when the container size changes (responsive breakpoints, window resize).
        void containerWidth.value;
        const el = containerRef.value;
        if (!el) {
            return;
        }
        const style = getComputedStyle(el);
        gapPx.value = parsePx(style.columnGap);
        inlinePaddingPx.value = parsePx(style.paddingInlineStart);
        blockStartPaddingPx.value = parsePx(style.paddingBlockStart);
        blockEndPaddingPx.value = parsePx(style.paddingBlockEnd);
    });

    const effectiveMaxWidth = computed(() => {
        if (containerWidth.value === 0) {
            return Number.POSITIVE_INFINITY;
        }
        const minCompanion = unref(options.minCompanionWidth) ?? DEFAULT_MIN_COMPANION_WIDTH_PX;
        const available = containerWidth.value - inlinePaddingPx.value * 2 - gapPx.value - minCompanion;
        return Math.max(options.minWidth, available);
    });

    const clamp = (value: number): number => {
        if (value < options.minWidth) {
            return options.minWidth;
        }
        if (value > effectiveMaxWidth.value) {
            return effectiveMaxWidth.value;
        }
        return value;
    };

    const storedWidth = useLocalStorage<number | null>(options.storageKey, options.defaultWidth ?? null);
    const width = ref<number | null>(storedWidth.value === null ? null : clamp(storedWidth.value));

    watch(storedWidth, (next) => {
        width.value = next === null ? null : clamp(next);
    });

    watch(effectiveMaxWidth, () => {
        if (storedWidth.value === null) {
            return;
        }
        const clamped = clamp(storedWidth.value);
        if (clamped !== storedWidth.value) {
            storedWidth.value = clamped;
        }
        if (clamped !== width.value) {
            width.value = clamped;
        }
    });

    const isResizing = ref(false);

    const startResize = (event: MouseEvent | TouchEvent): void => {
        if (!options.enabled.value) {
            return;
        }
        const startX = readClientX(event);
        if (startX === null) {
            return;
        }
        event.preventDefault();

        const startWidth = panelWidth.value || width.value || options.minWidth;
        const isRtl = document.documentElement.dir === 'rtl';
        const sideSign = side === 'end' ? -1 : 1;

        isResizing.value = true;

        const onMove = (moveEvent: MouseEvent | TouchEvent): void => {
            if ('touches' in moveEvent) {
                moveEvent.preventDefault();
            }
            const currentX = readClientX(moveEvent);
            if (currentX === null) {
                return;
            }
            const deltaRaw = currentX - startX;
            const inlineDelta = isRtl ? -deltaRaw : deltaRaw;
            const next = clamp(startWidth + sideSign * inlineDelta);
            storedWidth.value = next;
            width.value = next;
        };

        const onEnd = (): void => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onEnd);
            document.removeEventListener('touchmove', onMove);
            document.removeEventListener('touchend', onEnd);
            document.removeEventListener('touchcancel', onEnd);
            document.body.classList.remove('select-none');
            document.body.style.removeProperty('cursor');
            isResizing.value = false;
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onEnd);
        document.addEventListener('touchmove', onMove, { passive: false });
        document.addEventListener('touchend', onEnd);
        document.addEventListener('touchcancel', onEnd);
        document.body.classList.add('select-none');
        document.body.style.cursor = 'col-resize';
    };

    const panelStyle = computed<CSSProperties | undefined>(() => {
        if (!options.enabled.value || width.value === null) {
            return undefined;
        }
        return {
            width: `${width.value}px`,
            maxWidth: 'none',
            flex: '0 0 auto',
        };
    });

    const handleStyle = computed<CSSProperties>(() => {
        const currentWidth = width.value ?? panelWidth.value;
        const offset = `${inlinePaddingPx.value + currentWidth}px`;
        return {
            ...(side === 'end' ? { insetInlineEnd: offset } : { insetInlineStart: offset }),
            insetBlockStart: `${blockStartPaddingPx.value}px`,
            insetBlockEnd: `${blockEndPaddingPx.value}px`,
            width: `${gapPx.value}px`,
        };
    });

    return {
        containerRef,
        panelRef,
        width,
        isResizing,
        panelStyle,
        handleStyle,
        startResize,
    };
};
